import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const FILTER_KEYS = [
  "storey",
  "bedrooms",
  "bathrooms",
  "blockWidth",
  "blockDepth",
  "areaSqMin",
  "areaSqMax",
  "sort",
];

/**
 * URL-state-synced filter helper. All current filter values come from the
 * query string; setFilters writes back through useSearchParams so the
 * back/forward buttons replay each filter step.
 *
 *   const [filters, setFilters, resetFilters] = useUrlFilters({
 *     defaults: { sort: "sortOrder:asc" },
 *   });
 */
export function useUrlFilters({ defaults = {}, keys = FILTER_KEYS } = {}) {
  const [params, setParams] = useSearchParams();

  const filters = useMemo(() => {
    const out = { ...defaults };
    for (const k of keys) {
      const v = params.get(k);
      if (v !== null) out[k] = v;
    }
    return out;
  }, [params, defaults, keys]);

  const setFilters = (next) => {
    const merged = { ...filters, ...next };
    const sp = new URLSearchParams();
    // Preserve any non-filter params (e.g. ?page=2).
    for (const [k, v] of params.entries()) {
      if (!keys.includes(k)) sp.set(k, v);
    }
    for (const k of keys) {
      const v = merged[k];
      if (v !== "" && v !== undefined && v !== null) {
        // Skip writing the default sort so URLs stay clean.
        if (k === "sort" && v === defaults.sort) continue;
        sp.set(k, String(v));
      }
    }
    setParams(sp, { replace: false });
  };

  const resetFilters = () => {
    const sp = new URLSearchParams();
    for (const [k, v] of params.entries()) {
      if (!keys.includes(k)) sp.set(k, v);
    }
    setParams(sp, { replace: false });
  };

  return [filters, setFilters, resetFilters];
}

/**
 * Page-only URL state (for paginated lists).
 */
export function useUrlPage(defaultPage = 1) {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") ?? defaultPage);

  const setPage = (next) => {
    const sp = new URLSearchParams(params);
    if (next === defaultPage) sp.delete("page");
    else sp.set("page", String(next));
    setParams(sp, { replace: false });
  };

  return [page, setPage];
}
